import type { fonts } from './ui';
import { Settings } from './settings';
const {TextStyle, TextSize, SfxStyle} = Settings;

export const LAYOUT = Object.freeze({
    MAX_WIDTH: 2160,
    MAX_HEIGHT: 1080,
    SAFE_WIDTH: Math.floor(1080 / 3 * 4),
    SAFE_HEIGHT: 1080,
});

export const PING_SFX = Object.freeze({
    [SfxStyle.SFX_1]: 'PING Moog Sounds_01.L.{opus,caf,mp3}',
    [SfxStyle.SFX_2]: 'Moog Sounds_23.{opus,caf,mp3}',
    [SfxStyle.SFX_3]: 'Moog_13_Ping.{opus,caf,mp3}'
});

interface FontColors {
    text: number;
    bg: number;
}
type FontSize = number;
interface FontContext {
    [TextStyle.WHITE_ON_BLACK]: FontColors;
    [TextStyle.YELLOW_ON_BLACK]: FontColors;
    [TextStyle.BLACK_ON_WHITE]: FontColors;
    [TextSize.LARGE]: FontSize;
    [TextSize.MEDIUM]: FontSize;
    [TextSize.SMALL]: FontSize;
}
// explicitly list allowable ids in fonts object so that the resulting object is strongly typed
// to allow autocompletion and reduce typos
const typedFonts: {[id in fonts.AllStyleNames]: FontContext} = {
    // ============ GAMEPLAY FONT SETTINGS =============
    GameButton: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0xffffff, text: 0x010101 },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 20,
        [TextSize.MEDIUM]: 20,
        [TextSize.SMALL]: 20
    },
    GameCoordButton: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0xffffff, text: 0x010101 },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 24,
        [TextSize.MEDIUM]: 24,
        [TextSize.SMALL]: 24
    },
    GameLabel: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0xffffff, text: 0xffffff },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 25,
        [TextSize.MEDIUM]: 25,
        [TextSize.SMALL]: 25
    },
    GridLabel: {
        // [TextStyle.WHITE_ON_BLACK]: { bg: 0x16052b, text: 0x20f6db },
        [TextStyle.WHITE_ON_BLACK]: { bg: 0x000000, text: 0xffffff },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 40,
        [TextSize.MEDIUM]: 34,
        [TextSize.SMALL]: 28
    },
    CompassNormal: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0x20f6db, text: 0x17042b },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 27,
        [TextSize.MEDIUM]: 27,
        [TextSize.SMALL]: 27
    },
    CompassActive: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0xffffff, text: 0x17042b },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0xffff00, text: 0x000000 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0x000000, text: 0xffffff },
        [TextSize.LARGE]: 27,
        [TextSize.MEDIUM]: 27,
        [TextSize.SMALL]: 27
    },
    // ================ Title Font Settings ================
    PlayButton: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0xffffff, text: 0x000000 },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 45,
        [TextSize.MEDIUM]: 45,
        [TextSize.SMALL]: 45
    },
    // ================ Settings Font Settings ================
    SettingsTitle: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0xffffff, text: 0x000000 },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 70,
        [TextSize.MEDIUM]: 70,
        [TextSize.SMALL]: 70
    },
    SettingsIntroButton: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0xffffff, text: 0x000000 },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 38,
        [TextSize.MEDIUM]: 38,
        [TextSize.SMALL]: 38
    },
    SettingsTab: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0xffffff, text: 0x000000 },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 41,
        [TextSize.MEDIUM]: 41,
        [TextSize.SMALL]: 41
    },
    SettingsTabActive: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0x000000, text: 0xffffff },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0xffff00, text: 0x000000 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0x000000, text: 0xffffff },
        [TextSize.LARGE]: 42,
        [TextSize.MEDIUM]: 42,
        [TextSize.SMALL]: 42
    },
    SettingsGroupTitle: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0xffffff, text: 0x000000 },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 35,
        [TextSize.MEDIUM]: 35,
        [TextSize.SMALL]: 35
    },
    OnOffToggle: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0xffffff, text: 0x000000 },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 23,
        [TextSize.MEDIUM]: 23,
        [TextSize.SMALL]: 23
    },
    SliderLabel: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0xffffff, text: 0x000000 },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 24,
        [TextSize.MEDIUM]: 24,
        [TextSize.SMALL]: 24
    },
    YesNoToggle: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0xffffff, text: 0x000000 },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 28,
        [TextSize.MEDIUM]: 28,
        [TextSize.SMALL]: 28
    },
    RadioOption: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0xffffff, text: 0x000000 },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 24,
        [TextSize.MEDIUM]: 24,
        [TextSize.SMALL]: 24
    },
    TextPreview: {
        [TextStyle.WHITE_ON_BLACK]: { bg: 0x000000, text: 0xffffff },
        [TextStyle.YELLOW_ON_BLACK]: { bg: 0x000000, text: 0xffff00 },
        [TextStyle.BLACK_ON_WHITE]: { bg: 0xffffff, text: 0x000000 },
        [TextSize.LARGE]: 46,
        [TextSize.MEDIUM]: 36,
        [TextSize.SMALL]: 26
    },
};
export const FONTS = Object.freeze(typedFonts);

export enum StaticLayoutItem {
    Player = 'p',
    Part = 'x',
    Distractor = 'd',
    Battery = 'b',
}
export interface LevelData
{
    SQUARE_SIZE: number;
    GRID_WIDTH: number;
    GRID_HEIGHT: number;
    DISTRACTORS: number;
    BATTERIES: number;
    PARTS?: number;
    COORD_MOVE: boolean;
    FIXED_START: boolean;
    BAT_REWARD: number;
    CHARACTER: 'jackie'|'inez'|'matt';
    WALL: 'rock'|'ice'|'donut'|'log'|'flower'|'metal';
    /*
     * Static layouts are a single long array, rows from top to bottom:
     * [<x1, y2>, <x2, y2>, <x1, y1>, <x2, y1>]
     */
    STATIC_LAYOUT?: (StaticLayoutItem|null)[];
    TUTORIAL?: 'basics'|'coord';
}

const LEVELS: LevelData[] = [
    // TUTORIAL
    {
        SQUARE_SIZE: 128,
        GRID_WIDTH: 6,
        GRID_HEIGHT: 2,
        DISTRACTORS: 0,
        BATTERIES: 0,
        PARTS: 1,
        COORD_MOVE: false,
        FIXED_START: true,
        BAT_REWARD: 0,
        CHARACTER: 'jackie',
        WALL: 'log',
        // Static layouts are rows of columns
        STATIC_LAYOUT: [
            null, null, null, null, null, null,
            StaticLayoutItem.Player, null, null, null, StaticLayoutItem.Part, null
        ],
        TUTORIAL: 'basics'
    },
    // LEVEL 1
    {
        SQUARE_SIZE: 188,
        GRID_WIDTH: 4,
        GRID_HEIGHT: 4,
        DISTRACTORS: 1,
        BATTERIES: 1,
        COORD_MOVE: false,
        FIXED_START: true,
        BAT_REWARD: 5,
        CHARACTER: 'jackie',
        WALL: 'rock',
    },
    // LEVEL 2
    {
        SQUARE_SIZE: 128,
        GRID_WIDTH: 6,
        GRID_HEIGHT: 6,
        DISTRACTORS: 3,
        BATTERIES: 1,
        COORD_MOVE: false,
        FIXED_START: true,
        BAT_REWARD: 10,
        CHARACTER: 'matt',
        WALL: 'ice',
    },
    // LEVEL 3
    {
        SQUARE_SIZE: 84,
        GRID_WIDTH: 9,
        GRID_HEIGHT: 9,
        DISTRACTORS: 5,
        BATTERIES: 2,
        COORD_MOVE: false,
        FIXED_START: true,
        BAT_REWARD: 15,
        CHARACTER: 'inez',
        WALL: 'donut',
    },
    // LEVEL 4
    {
        SQUARE_SIZE: 188,
        GRID_WIDTH: 4,
        GRID_HEIGHT: 4,
        DISTRACTORS: 1,
        BATTERIES: 1,
        COORD_MOVE: true,
        FIXED_START: true,
        BAT_REWARD: 20,
        CHARACTER: 'jackie',
        WALL: 'log',
        TUTORIAL: 'coord',
    },
    // LEVEL 5
    {
        SQUARE_SIZE: 128,
        GRID_WIDTH: 6,
        GRID_HEIGHT: 6,
        DISTRACTORS: 3,
        BATTERIES: 1,
        COORD_MOVE: true,
        FIXED_START: true,
        BAT_REWARD: 25,
        CHARACTER: 'matt',
        WALL: 'flower',
    },
    // LEVEL 6
    {
        SQUARE_SIZE: 84,
        GRID_WIDTH: 9,
        GRID_HEIGHT: 9,
        DISTRACTORS: 5,
        BATTERIES: 2,
        COORD_MOVE: true,
        FIXED_START: true,
        BAT_REWARD: 30,
        CHARACTER: 'inez',
        WALL: 'metal',
    }
];

// allow some configuration while testing
let keepLocated = null;
let keepFound = null;
// URLSearchParams should be supported by everything but Safari 10 (but is supported by Safari 10.1)
// just in case, wrapping in try/catch because this isn't an important thing and if it isn't
// supported it shouldn't be allowed to screw things up
try {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('keepLocated'))
    {
        keepLocated = Boolean(searchParams.get('keepLocated'));
    }
    if (searchParams.has('keepFound'))
    {
        keepFound = Boolean(searchParams.get('keepFound'));
    }
}
catch (e) {}

export const GAMEPLAY = Object.freeze({
    // Percent chance to get a suggestion on where to ping
    HINT_LOCATION_CHANCE: {
        [Settings.HintFrequency.NONE]: 0,
        [Settings.HintFrequency.SOME]: 0.3,
        [Settings.HintFrequency.EXTRA]: 0.6
    },
    // Milliseconds of inactivity before playing a reminder line
    INACTIVITY_TIMER: {
        [Settings.HintFrequency.NONE]: Infinity,
        [Settings.HintFrequency.SOME]: 30000,
        [Settings.HintFrequency.EXTRA]: 15000
    },
    TOTAL_BATS: 105,
    // Grid is centered in this box, in case it isn't square
    GRID_AREA: {x: 563, y: 228, w: 756, h: 756},
    // Movement time in milliseconds per square moved, for both keys/swipe and coords
    MOVE_TIME_PER_SQUARE: 500,
    // Starting # of battery charges
    STARTING_BATTERY: 20,
    // Maximum # of battery charges
    MAX_BATTERY: 25,
    // Charges gained when finding a battery
    CHARGES_PER_BATTERY_FOUND: 5,
    // default: false - keep located items visible as ?
    HIDE_LOCATED: keepLocated === null ? false : !keepLocated,
    // If HIDE_LOCATED is true, milliseconds of visibility
    LOCATED_HIDE_TIME_MS: 2000,
    // default: true - remove picked up items from grid
    HIDE_FOUND: keepFound === null ? true : !keepFound,
    // Milliseconds to show a found item
    FOUND_SHOW_TIME: 5000,
    // Labels on X axis, 1-n
    X_LABELS: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'],
    // Labels on y axis, 1-n
    Y_LABELS: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
    // game levels: change these above
    LEVELS
});